// Itinerary data for the Recalibrate Retreat — A Glimpse of the Journey

export const DAYS = [
  {
    day: 1,
    ribbonDay: "Day 1",
    ribbonElement: "Arrival · Zen Boutique Sanctuary",
    label: "The Arrival",
    title: "The Arrival",
    chakra: "Land. Breathe. Feel the shift begin.",
    img: "/images/day-1.jpeg",
    imgAlt: "Zen Boutique pool sanctuary nestled among palms — Day 1 arrival",
    body: "The moment your feet touch Zanzibar, something in you starts to wake up — whatever you carried here, you can finally set it down. Warm air, palm shade, the hush of the pool — you are received into a sanctuary that will hold you for the next eight days. Tonight we open the circle with an intention-setting ceremony, meet the community you're about to share this with, and gently step into retreat rhythm.",
    bulletsLabel: "Today's activation",
    bullets: [
      "Welcome bodywork — a first fascia release to unwind the flight",
      "Opening ceremony under the stars",
      "Grounding breathwork and intention-setting",
      "Slow, sacred first meal with the community",
    ],
    closing: "You have arrived. Your new beginning starts tonight.",
  },
  {
    day: 2,
    ribbonDay: "Day 2",
    ribbonElement: "Root Activation · Happy Farm · Kinyasini Village",
    label: "The Grounding",
    title: "The Grounding",
    chakra: "Root Activation",
    img: "/images/day-2.jpeg",
    imgAlt: "A curious donkey grazing in the lush palm forest of Kinyasini Village — Day 2 grounding immersion",
    body: "Today we plant new roots. We leave the resort behind and disappear into the lush interior of Zanzibar, where a working farm becomes our teacher. Bare feet on rich soil, hands in spice bark, laughter with the local hosts — this is grounding as a body experience, not a concept. The root activation is what happens when you remember you belong to the earth.",
    bulletsLabel: "Today's activation",
    bullets: [
      "Guided hike through a palm forest thick with birdsong",
      "Living spice tour — cinnamon, cardamom, clove straight from the tree",
      "Fascia stretch and mobility session in the shade",
      "Root Chakra unwinding — release what's been holding you tight",
      "Slow shared lunch with our farm hosts",
      "Hands-on Makuti weaving — the meditative craft of the island",
    ],
    closing: "You leave the day rooted, open, and alive to everything that is growing in you.",
  },
  {
    day: 3,
    ribbonDay: "Day 3",
    ribbonElement: "Sacral & Solar Activation · Creative Fire · Personal Power",
    label: "The Remembrance",
    title: "The Remembrance",
    chakra: "Sacral & Solar Activation",
    img: "/images/day-3.jpeg",
    imgAlt: "A guided yoga and movement session beneath an open-air pavilion surrounded by tropical greenery — Day 3 The Remembrance",
    richContent: [
      { type: "p", text: "Today we call back the electric version of you — the one living underneath the roles, the résumé, the performance. Guided through sacral and solar activations, we soften the belly, warm the core, and light up creativity, sensuality, self-worth, and unapologetic personal power." },
      { type: "p", text: "Morning bodywork opens the fascia around the hips and low back — the storage rooms of the sacral and solar centers. Then breath, sound, and movement lift what has been sitting there for years." },
      { type: "italic", text: "Fascia release · Breathwork · Sacral & Solar activation · Somatic dance" },
      { type: "p", text: "The day closes in celebration — a semi-private Afro & Tribal dance experience with renowned choreographer Melo Omat. Sweat becomes prayer. Hips remember. The whole room comes alive — and so do you." },
    ],
  },
  {
    day: 4,
    ribbonDay: "Day 4",
    ribbonElement: "Wild Silence · Selous (Nyerere) Safari",
    label: "The Wilderness",
    title: "The Wilderness",
    chakra: "Nature as Activation",
    img: "/images/day-4.jpeg",
    imgAlt: "A lioness resting on sun-warmed rocks beneath acacia branches — Day 4 safari in Selous",
    body: "Before sunrise, we lift into the sky toward something bigger than all of us — Selous, one of the last great wildernesses on Earth. Elephants at the waterline. Lions in the golden grass. A silence so full it rearranges your nervous system. Out here, in the presence of untamed intelligence, something ancient in you remembers itself. You are not separate from nature. You are nature — and today, nature does the work.",
    bulletsLabel: "Today's activation",
    bullets: [
      "Sunrise transfer + domestic flight to Selous",
      "Full-day guided safari adventure",
      "Bush lunch under an acacia in the heart of the park",
      "Pre-flight fascia and stretch session to release the day",
      "Evening return to Zanzibar for restorative bodywork",
    ],
    closing: "The wilderness teaches stillness better than any studio ever could. You come home to the resort with a wider heart and a bigger vision for your life.",
  },
  {
    day: 5,
    ribbonDay: "Day 5",
    ribbonElement: "Heart & Throat Activation · Truth · Voice",
    label: "The Opening",
    title: "The Opening",
    chakra: "Heart & Throat Activation",
    img: "/images/day-5.jpeg",
    imgAlt: "Surreal portrait of a soul releasing a primal scream — Day 5 The Opening, voice and truth awakening",
    richContent: [
      { type: "p", text: "Today we open the heart — and the places the world taught you to keep quiet. Chest, jaw, throat — the highways of unsaid things. Bodywork opens the ribcage and the space along the collarbones. Breath fills what has been holding its breath for years." },
      { type: "italic", text: "Fascia chest-wall decompression · Heart-opening breathwork · Rage Workshop · Voice reclamation" },
      { type: "p", text: "The Rage Workshop is where the polite falls off. Suppressed grief, unspoken no's, the years of holding it together for everyone else — they get a room, a witness, and finally, a way out. What comes after is a softness only the fully expressed know." },
      { type: "p", text: "Tonight, dinner tastes different. Your voice sounds different. Your heart is open, and love finally has room to find you." },
    ],
  },
  {
    day: 6,
    ribbonDay: "Day 6",
    ribbonElement: "PEAK ACTIVATION · Safari Blue · Indian Ocean",
    label: "The Limitless",
    title: "Safari Blue — Peak Activation",
    chakra: "Third Eye & Crown Activation",
    img: "/images/day-6.jpeg",
    imgAlt: "Aerial view of Pungume Island sandbank surrounded by turquoise waters and dhows — Day 6 Safari Blue Peak Activation",
    richContent: [
      { type: "p", text: "Today is the peak. We take everything awakened this week out into the wild. The crystal-clear waters of Safari Blue become our sacred space — a vast, crystalline vibrancy running through every cell of our being." },
      { type: "p", text: "As we move through turquoise waters surrounded by untouched nature, we activate what has been dormant, release what no longer serves, and let the ocean do what no therapist's table can." },
      { type: "italic", text: "This is nature as healer. Water as medicine." },
      { type: "p", text: "You will feel it in your fascia, your chakras, your soul. This is the day everything shifts — and you remember that anything is possible." },
    ],
    bulletsLabel: "The peak activation unfolds through",
    bullets: [
      "Sailing aboard a traditional dhow across the Indian Ocean",
      "Snorkeling in the crystal waters of Zanzibar's reef",
      "Fresh tropical fruit on a secluded sandbank at low tide",
      "Seafood feast on beautiful Pungume Island",
      "Sunset bodywork and integration back at the sanctuary",
    ],
  },
  {
    day: 7,
    ribbonDay: "Day 7",
    ribbonElement: "Integration · The Beginning · A Community Reborn",
    label: "The Beginning",
    title: "The Beginning",
    chakra: "This is not the end. It is your ignition.",
    img: "/images/day-7.jpeg",
    imgAlt: "Two sisters embracing in a tight, joyful hug — Day 7 closing of the retreat",
    richContent: [
      { type: "p", text: "You arrived carrying a version of yourself the world had shaped. Today, a different you walks out — lighter, brighter, more here. Mind, body, and soul in the same room for the first time in a long time." },
      { type: "italic", text: "Final fascia integration · Closing breathwork · Community circle · Sunrise farewell" },
      { type: "p", text: "The morning is slow and sacred. One last bodywork session to lock in what has moved. A closing circle where the community you've built puts words to what happened here. Then a farewell that feels like a beginning." },
      { type: "p", text: "You leave with embodied tools, a bag of Zanzibar sun, and a community that has seen you become. The retreat ends. The activation continues." },
      { type: "italic", text: "Your next chapter starts the day you land back home — brighter than you ever imagined." },
    ],
  },
];
